import { useState } from 'react';
import CreateCreditNoteHeader from './createCrediNoteHeader';
import { getCurrentDate } from '../../../../../utils/dateUtils';
import CreditNoteForm from './creditNoteForm';
import CreditNoteTable from './creditNoteTable';
import { toast } from 'react-toastify';
import { createCreditNote, updateCreditNote } from '../../../../../services/sales_credit_note';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AutoSaveModal from '../../../../components/autoSaveModal';

const SalesCreateCreditNote = () => {
	const [error, setError] = useState({});
	const [creditNote, setCreditNote] = useState({
		customer_id: null,
		credit_note_number: null,
		date: getCurrentDate(),
		credit_note_type: 1,
		amount_paid: '',
		reference: '',
		notes: '',
	});
	const [salesReturnNumbers, setSalesReturnNumbers] = useState([]);
	const [creditNoteTable, setCreditNoteTable] = useState([]);
	const navigate = useNavigate();
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const suburl = localStorage.getItem('suburl');
	const param = useParams();
	const id = param?.id;
	const [loading, setLoading] = useState(false);
	const [isNextLocation, setIsNextLocation] = useState(null);
	const [isLocationBlocker, setIsLocationBlocker] = useState(true);
	const [isCreditNoteAutoSave, setIsCreditNoteAutoSave] = useState(false);
	const calculateTotalPayment = () => {
		if (creditNote?.credit_note_type === 1) {
			return creditNoteTable?.reduce((total, bill) => Number(total) + Number(bill.amount), 0);
		} else {
			return creditNoteTable?.reduce(
				(total, bill) => Number(total) + Number(bill.excess_payment_amount),
				0,
			);
		}
	};

	const handleDeleteRow = (id) => {
		setCreditNoteTable((prevArray) => prevArray.filter((item) => item.id !== id));
		setSalesReturnNumbers((prevArray) => prevArray.filter((item) => item.id !== id));
	};

	const checkValidation = (status) => {
		let newError = { ...error };
		if (
			!creditNote.customer_id ||
			creditNote.customer_id === '' ||
			creditNote.customer_id === undefined
		) {
			newError.customer_id = 'Please select a valid customer from the list.';
		} else {
			delete newError.customer_id;
		}

		if (
			!creditNote.credit_note_number ||
			creditNote.credit_note_number === '' ||
			creditNote.credit_note_number === undefined
		) {
			newError.credit_note_number = 'Please enter credit note number in the field';
		} else {
			delete newError.credit_note_number;
		}

		if (
			!creditNote.date ||
			creditNote.date === '' ||
			creditNote.date === undefined ||
			(creditNote.date !== '' && newError.date)
		) {
			newError.date = newError.date ?? 'Please enter credit note date in the field';
		} else {
			delete newError.date;
		}

		if (
			!creditNote.credit_note_type ||
			creditNote.credit_note_type === '' ||
			creditNote.credit_note_type === undefined
		) {
			newError.credit_note_type = 'Please select a valid payment method from the list.';
		} else {
			delete newError.credit_note_type;
		}
		if (creditNote.credit_note_type === 1) {
			if (salesReturnNumbers.length === 0) {
				newError.sales_return_number = 'Please select a valid sales return number from the list.';
			} else {
				delete newError.return_number;
			}
		} else {
			delete newError.amount_paid;
		}
		if (creditNote.credit_note_type === 3) {
			if (
				!creditNote.amount_paid ||
				creditNote.amount_paid === '' ||
				creditNote.amount_paid === undefined ||
				(creditNote.amount_paid !== '' && newError.amount_paid)
			) {
				newError.amount_paid = 'Please enter amount in the field';
			} else {
				delete newError.amount_paid;
			}
		} else {
			delete newError.return_number;
		}

		if (Object.keys(newError).length > 0) {
			setError(newError);
			return true;
		} else {
			return false;
		}
	};

	const handleSubmitCreditNote = async (status, isCreditNote = false) => {
		const isErrorShow = checkValidation(status);
		if (isErrorShow) {
			toast.error('Please fill all mandatory fields before saving');
		} else {
			try {
				setLoading(true);
				const body = {
					customer_id: creditNote?.customer_id,
					credit_note_number: creditNote?.credit_note_number,
					date: creditNote?.date,
					credit_note_date: creditNote?.date,
					notes: creditNote?.notes,
					references: creditNote?.reference,
					credit_note_type: creditNote?.credit_note_type,
					credit_note_sales_return_ids:
						creditNote?.credit_note_type === 1
							? salesReturnNumbers.map((item) => {
									return item?.id;
								})
							: [],
					status: status,
					total_credit_amount:
						creditNote.credit_note_type !== 3
							? calculateTotalPayment().toFixed(2)
							: creditNote.amount_paid,
					credit_note_payment_receive_ids:
						creditNote?.credit_note_type === 2
							? creditNoteTable.map((item) => {
									return item?.id;
								})
							: [],
				};
				if (id) {
					var res = await updateCreditNote(id, body);
					if (res?.success) {
						toast.success('Credit Note updated Successfully');
						navigate(`/user/${suburl}/sales/credit-note`);
					} else {
						toast.error(res.message ?? 'something went wrong');
					}
				} else {
					const res = await createCreditNote(body);
					if (res?.success) {
						toast.success('Credit Note Created Successfully');
						setIsLocationBlocker(false);
						setIsCreditNoteAutoSave(false);
						setTimeout(() => {
							navigate(`/user/${suburl}/sales/credit-note`);
						}, 300);
					} else {
						toast.error(res.message ?? 'something went wrong');
					}
				}
			} catch (error) {
				toast.error(error.message ?? 'something went wrong');
			} finally {
				setLoading(false);
			}
		}
	};

	useBlocker((tx) => {
		if (!id && isLocationBlocker) {
			setIsNextLocation(tx);
			setIsCreditNoteAutoSave(true);
			return true;
		}
		return false;
	});

	return (
		<div>
			<CreateCreditNoteHeader
				id={id}
				user={user}
				loading={loading}
				suburl={suburl}
				navigate={navigate}
				setIsCreditNoteAutoSave={setIsCreditNoteAutoSave}
				handleSubmitCreditNote={handleSubmitCreditNote}
			/>
			<CreditNoteForm
				creditNote={creditNote}
				setSalesReturnNumbers={setSalesReturnNumbers}
				salesReturnNumbers={salesReturnNumbers}
				setCreditNote={setCreditNote}
				error={error}
				setError={setError}
				setCreditNoteTable={setCreditNoteTable}
				creditNoteTable={creditNoteTable}
			/>
			{creditNote.credit_note_type !== 3 && (
				<CreditNoteTable
					column={
						creditNote.credit_note_type === 1
							? ['Date Created', 'Sales Return Number', 'Amount', 'Actions']
							: ['Invoice#', 'PR#', 'Total Amount', 'Excess Amount']
					}
					rows={creditNoteTable}
					creditNoteType={creditNote.credit_note_type}
					handleDelete={(id) => {
						if (creditNote.credit_note_type === 1) {
							handleDeleteRow(id);
						}
					}}
				/>
			)}
			<div className="credit-summary-container">
				<div className="credit-summary-bar">
					<div className="d-flex align-items-center justify-content-end h-100 px-4">
						<p className="credit-summary-text mb-0 w-100">
							Credit Total: ₹
							{creditNote?.credit_note_type !== 3
								? calculateTotalPayment().toFixed(2)
								: creditNote?.amount_paid}
							/-
						</p>
					</div>
				</div>
			</div>
			{isCreditNoteAutoSave && (
				<AutoSaveModal
					isOpenModel={isCreditNoteAutoSave}
					setIsOpenModel={setIsCreditNoteAutoSave}
					user={user}
					permission={'sales.credit_notes.create'}
					type={'Credit Note'}
					withoutSave={() => {
						setIsLocationBlocker(false);
						setIsCreditNoteAutoSave(false);
						setTimeout(() => {
							navigate(isNextLocation.nextLocation?.pathname);
						}, 300);
					}}
					SaveAsContinue={() => {
						setIsCreditNoteAutoSave(false);
						handleSubmitCreditNote(1);
					}}
				/>
			)}
		</div>
	);
};
export default SalesCreateCreditNote;
